<template>
  <e-dialog v-model="isOpened" @after-leave="onClose">
    <template #title>
      <div class="caption">Обращение в Техподдержку</div>
    </template>

    <e-form v-if="!sentStatus" v-model="data.isValid" @submit="submit">
      <e-textarea
        v-model="data.description"
        label="Дополнительная информация (необязательно)"
        rows="9"
        :rules="[maxLength(32767)]"
      />

      <e-textarea :model-value="message" readonly class="mt-4" rows="7">
        <template #append-inner>
          <copy-to-clipboard :text="message" />
        </template>
      </e-textarea>

      <div class="paragraph-l font-600 my-6">Контакты для связи</div>

      <e-input v-model="data.userName" label="Имя" :rules="[required(), maxLength(25)]" />

      <div class="support-dialog__contacts">
        <phone-field v-model="data.userPhone" :rules="[required()]" />
        <e-input v-model="data.userEmail" label="Email" :rules="[required(), emailRule()]" />
      </div>

      <div class="d-flex mt-4 col-gap-4 justify-end">
        <e-btn variant="secondary" @click="close">Отмена</e-btn>
        <e-btn variant="primary" type="submit" :loading="data.isLoading">Создать</e-btn>
      </div>
    </e-form>

    <message-sent
      v-else
      key="sent-message"
      class="feedback__message-sent"
      hide-send-again-button
      :sent-status="sentStatus"
      :message="errorMessage"
    />
  </e-dialog>
</template>

<script setup lang="ts">
import { reactive, ref, toRef } from 'vue';
import CopyToClipboard from '@/core/components/buttons/CopyToClipboard.vue';
import PhoneField from '@/core/components/fields/PhoneField.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, EForm, EInput, ETextarea } from '@/core/components/ui';
import { clearPhoneFormat } from '@/core/utils/string';
import { emailRule, maxLength, required } from '@/core/utils/validation';
import { useAuthStore } from '@/parts/auth/store/auth';
import MessageSent from '@/parts/support/components/MessageSent.vue';
import { useSupportStore } from '@/parts/support/store/support';

const props = defineProps<{
  message: string;
}>();

const { profile } = useAuthStore();

const isOpened = defineModel<boolean>({ required: true });

const { sendToHelpdesk } = useSupportStore();

function getInitialData() {
  return {
    isValid: false,
    isLoading: false,
    description: null,
    userName: profile.value?.profile?.name || null,
    userPhone: profile.value?.profile?.phoneNumber || null,
    userEmail: profile.value?.email || null,
  };
}

const data = reactive(getInitialData());

const resetForm = () => {
  Object.assign(data, getInitialData());
};

const close = () => {
  isOpened.value = false;
};

const errorMessage = toRef(
  () => `Обращение в Техподдержку.\n${data.description ? `\nОписание:\n${data.description}\n` : ''}
Техническое описание ошибки:
${props.message}\n
Контакты для связи:
Имя: ${data.userName}
Телефон: ${data.userPhone}
Email: ${data.userEmail}
Код пространства: ${profile.value?.supplierCode}`,
);

const sentStatus = ref<'success' | 'error'>();

const onClose = () => {
  sentStatus.value = undefined;
};

const submit = async () => {
  if (!data.isValid) {
    return;
  }

  data.isLoading = true;

  const result = await sendToHelpdesk({
    details: props.message,
    description: data.description,
    userName: data.userName!,
    phoneNumber: clearPhoneFormat(data.userPhone!),
    email: data.userEmail!,
  });

  if (!result || result.errors) {
    sentStatus.value = 'error';
  } else {
    sentStatus.value = 'success';
    resetForm();
  }

  data.isLoading = false;
};
</script>

<style scoped lang="scss">
.support-dialog {
  &__contacts {
    display: flex;
    align-items: flex-start;

    gap: 16px;

    margin-top: 16px;

    @include extra-xs {
      flex-wrap: wrap;
    }
  }
}
</style>
